import axios from 'axios';

class DrawingMetricsService {
    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL || 'https://api.angelwatchedu.org/api';
        this.metrics = {
            // Entry tracking
            totalEntries: 0,
            monthlyEntries: 0,
            dailyEntries: 0,

            // Conversion tracking
            popupViews: 0,
            popupInteractions: 0,
            formStarts: 0,
            formCompletions: 0,
            formAbandons: 0,

            // Geographic tracking
            entriesByState: {},
            entriesByCountry: {},

            // Time-based tracking
            entriesByHour: {},
            entriesByDay: {},
            entriesByMonth: {},

            // Device tracking
            entriesByDevice: {
                desktop: 0,
                mobile: 0,
                tablet: 0
            },

            // Source tracking
            entriesBySource: {
                direct: 0,
                social: 0,
                search: 0,
                referral: 0
            },

            // Form behavior
            formFieldInteractions: {
                firstName: { starts: 0, completions: 0, abandons: 0 },
                lastName: { starts: 0, completions: 0, abandons: 0 },
                email: { starts: 0, completions: 0, abandons: 0 },
                schoolName: { starts: 0, completions: 0, abandons: 0 }
            },

            // Performance metrics
            averageFormCompletionTime: 0,
            formCompletionRate: 0,
            popupConversionRate: 0,

            // Winner tracking
            totalWinners: 0,
            winnersByMonth: {},
            winnerEngagement: {
                accountActivations: 0,
                accountUsage: 0
            }
        };

        this.sessionData = {
            sessionId: this.generateSessionId(),
            startTime: Date.now(),
            pageViews: 0,
            interactions: 0,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language
        };
    }

    // Generate unique session ID
    generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    // Track popup view
    trackPopupView() {
        this.metrics.popupViews++;
        this.sessionData.pageViews++;
        this.sendMetric('popup_view', {
            sessionId: this.sessionData.sessionId,
            timestamp: Date.now(),
            referrer: this.sessionData.referrer,
            userAgent: this.sessionData.userAgent
        });
    }

    // Track popup interaction (click, hover, etc.)
    trackPopupInteraction(interactionType, data = {}) {
        this.metrics.popupInteractions++;
        this.sessionData.interactions++;
        this.sendMetric('popup_interaction', {
            sessionId: this.sessionData.sessionId,
            interactionType,
            timestamp: Date.now(),
            ...data
        });
    }

    // Track form start
    trackFormStart() {
        this.metrics.formStarts++;
        this.sendMetric('form_start', {
            sessionId: this.sessionData.sessionId,
            timestamp: Date.now(),
            formFields: ['firstName', 'lastName', 'email', 'schoolName']
        });
    }

    // Track form field interaction
    trackFormFieldInteraction(fieldName, action, data = {}) {
        if (this.metrics.formFieldInteractions[fieldName]) {
            this.metrics.formFieldInteractions[fieldName][action]++;
        }

        this.sendMetric('form_field_interaction', {
            sessionId: this.sessionData.sessionId,
            fieldName,
            action,
            timestamp: Date.now(),
            ...data
        });
    }

    // Track form completion
    trackFormCompletion(formData, completionTime) {
        this.metrics.formCompletions++;
        this.metrics.formAbandons = this.metrics.formStarts - this.metrics.formCompletions;
        this.metrics.formCompletionRate = (this.metrics.formCompletions / this.metrics.formStarts) * 100;
        this.metrics.averageFormCompletionTime = completionTime;

        // Track geographic data
        this.trackGeographicData(formData);

        // Track device data
        this.trackDeviceData();

        // Track source data
        this.trackSourceData();

        this.sendMetric('form_completion', {
            sessionId: this.sessionData.sessionId,
            timestamp: Date.now(),
            completionTime,
            formData: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                schoolName: formData.schoolName || null
            },
            metrics: {
                formCompletionRate: this.metrics.formCompletionRate,
                averageCompletionTime: this.metrics.averageFormCompletionTime
            }
        });
    }

    // Track form abandonment
    trackFormAbandon(abandonPoint, data = {}) {
        this.metrics.formAbandons++;
        this.sendMetric('form_abandon', {
            sessionId: this.sessionData.sessionId,
            abandonPoint,
            timestamp: Date.now(),
            ...data
        });
    }

    // Track drawing entry
    trackDrawingEntry(entryData) {
        this.metrics.totalEntries++;
        this.metrics.monthlyEntries++;
        this.metrics.dailyEntries++;

        const now = new Date();
        const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const dayKey = now.toISOString().split('T')[0];
        const hourKey = now.getHours();

        // Track by time
        this.metrics.entriesByMonth[monthKey] = (this.metrics.entriesByMonth[monthKey] || 0) + 1;
        this.metrics.entriesByDay[dayKey] = (this.metrics.entriesByDay[dayKey] || 0) + 1;
        this.metrics.entriesByHour[hourKey] = (this.metrics.entriesByHour[hourKey] || 0) + 1;

        this.sendMetric('drawing_entry', {
            sessionId: this.sessionId,
            timestamp: Date.now(),
            entryData,
            metrics: {
                totalEntries: this.metrics.totalEntries,
                monthlyEntries: this.metrics.monthlyEntries,
                dailyEntries: this.metrics.dailyEntries
            }
        });
    }

    // Track winner selection
    trackWinnerSelection(winnerData, drawingMonth) {
        this.metrics.totalWinners++;
        this.metrics.winnersByMonth[drawingMonth] = (this.metrics.winnersByMonth[drawingMonth] || 0) + 1;

        this.sendMetric('winner_selection', {
            sessionId: this.sessionId,
            timestamp: Date.now(),
            winnerData,
            drawingMonth,
            metrics: {
                totalWinners: this.metrics.totalWinners,
                winnersByMonth: this.metrics.winnersByMonth
            }
        });
    }

    // Track winner engagement
    trackWinnerEngagement(winnerId, engagementType, data = {}) {
        if (engagementType === 'account_activation') {
            this.metrics.winnerEngagement.accountActivations++;
        } else if (engagementType === 'account_usage') {
            this.metrics.winnerEngagement.accountUsage++;
        }

        this.sendMetric('winner_engagement', {
            sessionId: this.sessionId,
            winnerId,
            engagementType,
            timestamp: Date.now(),
            ...data
        });
    }

    // Track geographic data
    trackGeographicData(formData) {
        // This would typically use a geolocation API or IP-based location
        // For now, we'll track basic patterns
        if (formData.schoolName) {
            // Extract state from school name if possible
            const statePattern = /\b([A-Z]{2})\b/;
            const stateMatch = formData.schoolName.match(statePattern);
            if (stateMatch) {
                const state = stateMatch[1];
                this.metrics.entriesByState[state] = (this.metrics.entriesByState[state] || 0) + 1;
            }
        }
    }

    // Track device data
    trackDeviceData() {
        const userAgent = navigator.userAgent.toLowerCase();
        if (/mobile|android|iphone|ipad/.test(userAgent)) {
            this.metrics.entriesByDevice.mobile++;
        } else if (/tablet|ipad/.test(userAgent)) {
            this.metrics.entriesByDevice.tablet++;
        } else {
            this.metrics.entriesByDevice.desktop++;
        }
    }

    // Track source data
    trackSourceData() {
        const referrer = this.sessionData.referrer.toLowerCase();
        if (referrer.includes('facebook') || referrer.includes('instagram') || referrer.includes('twitter')) {
            this.metrics.entriesBySource.social++;
        } else if (referrer.includes('google') || referrer.includes('bing') || referrer.includes('yahoo')) {
            this.metrics.entriesBySource.search++;
        } else if (referrer) {
            this.metrics.entriesBySource.referral++;
        } else {
            this.metrics.entriesBySource.direct++;
        }
    }

    // Send metric to backend
    async sendMetric(eventType, data) {
        try {
            await axios.post(`${this.baseURL}/metrics/track`, {
                eventType,
                data,
                sessionData: this.sessionData,
                timestamp: Date.now()
            });
        } catch (error) {
            console.error('Failed to send metric:', error);
            // Store locally for retry
            this.storeMetricLocally(eventType, data);
        }
    }

    // Store metric locally for retry
    storeMetricLocally(eventType, data) {
        const storedMetrics = JSON.parse(localStorage.getItem('drawing_metrics') || '[]');
        storedMetrics.push({
            eventType,
            data,
            timestamp: Date.now()
        });
        localStorage.setItem('drawing_metrics', JSON.stringify(storedMetrics));
    }

    // Retry failed metrics
    async retryFailedMetrics() {
        const storedMetrics = JSON.parse(localStorage.getItem('drawing_metrics') || '[]');
        if (storedMetrics.length === 0) return;

        for (const metric of storedMetrics) {
            try {
                await axios.post(`${this.baseURL}/metrics/track`, metric);
                // Remove successful metric
                const updatedMetrics = storedMetrics.filter(m => m !== metric);
                localStorage.setItem('drawing_metrics', JSON.stringify(updatedMetrics));
            } catch (error) {
                console.error('Failed to retry metric:', error);
            }
        }
    }

    // Get current metrics
    getMetrics() {
        return {
            ...this.metrics,
            sessionData: this.sessionData,
            lastUpdated: Date.now()
        };
    }

    // Get metrics summary
    getMetricsSummary() {
        return {
            totalEntries: this.metrics.totalEntries,
            monthlyEntries: this.metrics.monthlyEntries,
            dailyEntries: this.metrics.dailyEntries,
            formCompletionRate: this.metrics.formCompletionRate,
            popupConversionRate: this.metrics.popupConversionRate,
            averageFormCompletionTime: this.metrics.averageFormCompletionTime,
            totalWinners: this.metrics.totalWinners,
            entriesByDevice: this.metrics.entriesByDevice,
            entriesBySource: this.metrics.entriesBySource
        };
    }

    // Export metrics for analysis
    exportMetrics() {
        const exportData = {
            metrics: this.metrics,
            sessionData: this.sessionData,
            exportDate: new Date().toISOString(),
            version: '1.0.0'
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `drawing_metrics_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    // Initialize metrics tracking
    initialize() {
        // Retry any failed metrics
        this.retryFailedMetrics();

        // Track page load
        this.trackPopupView();

        // Set up periodic metric sending
        setInterval(() => {
            this.retryFailedMetrics();
        }, 30000); // Retry every 30 seconds

        // Track page unload
        window.addEventListener('beforeunload', () => {
            this.sendMetric('session_end', {
                sessionId: this.sessionId,
                duration: Date.now() - this.sessionData.startTime,
                pageViews: this.sessionData.pageViews,
                interactions: this.sessionData.interactions
            });
        });
    }
}

// Create singleton instance
const drawingMetricsService = new DrawingMetricsService();

export default drawingMetricsService;
